import React, { useEffect, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import HttpManager from "../../../../services/HttpManager";
import SchedulesService from "../../../../services/campus/schedules.service";
import AsuetosHorario from "../../../../components/asuetosHorario/asuetosHorario";
import HistorialHorario from "../../../../components/historialHorario/historialHorario";
import useEsTelefono from "../../../../utils/esTelefono";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { nombreDeAlumno } from "../../../../utils/nombreCorto";
import { dinero, fechaDMY, fechasTexto, titulo, tituloHorario } from "./formato";
import { FichaHorario } from "./interfaces";
import { mensajeServidor } from "./horarioFormApi";
import CambiarHora from "./cambiarHora";
import { ChipEstado } from "./piezas";
import S from "./strings";
import "./horarios.css";

/*
	Ventana del horario (Horarios Claros): lo que antes eran cuatro botones por fila y una ficha de formulario. Arriba, qué es y qué
	se puede hacer; en medio los datos y la historia en una línea; abajo, pestañas con Alumnas, Sesiones y Asuetos, Precios e Historial.
*/
const http = HttpManager.getInstance();

export interface PermisosVentana {
	editar: boolean;
	borrar: boolean;
	matricula: boolean;
	lista: boolean;
}

interface Props {
	campusId: number;
	id: number;
	sym: string;
	aulas: { id: number; name: string }[];
	permisos: PermisosVentana;
	onClose: () => void;
	onEditar: (id: number) => void;
	onActualizarMatricula: (f: FichaHorario) => void;
	/** Cambió algo (se borró): la lista se vuelve a pedir. */
	onCambio: () => void;
}

type PestanaKey = "alumnas" | "sesiones" | "precios" | "historial";

const ETIQUETAS_PRECIO: Record<string, string> = {
	inscripcion: "Inscripción", inscripcionPropia: "Inscripción de Este Horario", mensualidad: "Mensualidad", ajusteMensualidad: "Sobrecargo del Horario",
	meses: "Meses a Facturar", recargo1: "Recargo de Mensualidad", recargo2: "Segundo Recargo", recargoInscripcion: "Recargo de Inscripción",
	diaRecargo1: "Día del Primer Recargo", diaRecargo2: "Día del Segundo Recargo",
	reposicionSesion: "Sesión de Recuperación", tarifaMaestra: "Pago a la Maestra", minimoAlumnas: "Mínimo de Alumnas", cupo: "Cupo"
};
const NO_ES_DINERO = /(^meses$|dia|day|minimo|cupo|limit)/i;

export const Dato: React.FC<{ t: string; children: React.ReactNode }> = ({ t, children }) => <div className="hcDato"><small>{t}</small><div>{children}</div></div>;

const VentanaHorario: React.FC<Props> = ({ campusId, id, sym, aulas, permisos, onClose, onEditar, onActualizarMatricula, onCambio }) => {
	const movil = useEsTelefono();
	const [f, setF] = useState<FichaHorario | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [tab, setTab] = useState<PestanaKey>("alumnas");
	const [alumnas, setAlumnas] = useState<any[] | null>(null);
	const [sesiones, setSesiones] = useState<any[] | null>(null);
	const [confirmando, setConfirmando] = useState<boolean>(false);
	const [borrando, setBorrando] = useState<boolean>(false);
	const [errorBorrar, setErrorBorrar] = useState<string | null>(null);
	// Horarios Claros fase 2: los dos botones del aviso «Nombre Distinto»
	const [cambiandoHora, setCambiandoHora] = useState<boolean>(false);
	const [rehaciendo, setRehaciendo] = useState<boolean>(false);
	const [guardandoNombre, setGuardandoNombre] = useState<boolean>(false);
	const [errorNombre, setErrorNombre] = useState<string | null>(null);

	const recargarFicha = () => {
		http.horariosService.ficha(campusId, id).then((r) => setF(r.data)).catch((e) => console.error(e));
		setSesiones(null);
		onCambio();
	};

	useEffect(() => {
		let vigente = true;
		setF(null); setCambiandoHora(false); setRehaciendo(false); setErrorNombre(null); setError(null); setAlumnas(null); setSesiones(null); setTab("alumnas");
		http.horariosService.ficha(campusId, id).then((r) => { if (vigente) setF(r.data); }).catch((e) => { console.error(e); if (vigente) setError(S.fichaError); });
		http.horariosService.alumnas(campusId, id).then((r) => {
			// Activas primero, luego las demás (retiradas, cambio de horario…) en el orden que da el servidor.
			const lista = Array.isArray(r.data) ? [...r.data] : [];
			lista.sort((x: any, y: any) => (Number(x.enrollmentStatusSortOrder) || 99) - (Number(y.enrollmentStatusSortOrder) || 99));
			if (vigente) setAlumnas(lista);
		}).catch(() => { if (vigente) setAlumnas([]); });
		return () => { vigente = false; };
	}, [campusId, id]);

	useEffect(() => {
		if (tab !== "sesiones" || sesiones) return;
		let vigente = true;
		http.scheduleService.getScheduleSessions(campusId, id).then((r) => { if (vigente) setSesiones(Array.isArray(r.data) ? r.data : []); }).catch(() => { if (vigente) setSesiones([]); });
		return () => { vigente = false; };
	}, [tab, sesiones, campusId, id]);

	const nombre = f ? tituloHorario(f, cursoCorto) : "";
	const sinAlumnas = f ? f.alumnas < 1 : false;
	const puedeBorrar = permisos.borrar && sinAlumnas;
	const puedeLista = permisos.lista && f && f.estado !== "fusionado" && f.estado !== "nuncaComenzo";
	const hayMenu = f && ((permisos.matricula) || puedeBorrar);

	const nombreDistinto = !!f && permisos.editar && f.problemas.includes("nombreDistinto");
	const usarClases = async () => {
		setGuardandoNombre(true); setErrorNombre(null);
		try {
			await http.horariosService.nombreDesdeSesiones(campusId, id, "Usar la hora de las clases");
			setRehaciendo(false);
			recargarFicha();
		} catch (e: any) {
			setErrorNombre(mensajeServidor(e).texto || S.ch.errorNombre);
		} finally { setGuardandoNombre(false); }
	};

	const borrar = async () => {
		setBorrando(true); setErrorBorrar(null);
		try {
			const r: any = await SchedulesService.removeSchedules(campusId, [id]);
			if (r.status === 200 || r.status === 204) { onCambio(); onClose(); return; }
			setErrorBorrar(S.borrarError);
		} catch (e: any) {
			console.error(e);
			setErrorBorrar(e?.response?.data?.message || S.borrarError);
		} finally { setBorrando(false); }
	};

	const preciosFilas = f ? Object.entries(f.precios || {}).filter(([, v]) => v !== null && v !== undefined && v !== "") : [];
	const valorPrecio = (k: string, v: any): string => (typeof v === "number" && !NO_ES_DINERO.test(k) ? dinero(sym, v) : String(v));
	const mensualidad = f && f.precios?.mensualidad != null && f.precios.mensualidad !== ""
		? dinero(sym, f.precios.mensualidad) + (f.precios.meses ? ` · ${f.precios.meses} meses` : "") : "";

	const tabs: { key: PestanaKey; label: string; cuenta?: number }[] = [
		{ key: "alumnas", label: S.vTabs.alumnas, cuenta: alumnas ? alumnas.filter((a: any) => Number(a.enrollmentStatusId) === 1).length : f?.alumnas },
		{ key: "sesiones", label: S.vTabs.sesiones },
		{ key: "precios", label: S.vTabs.precios },
		{ key: "historial", label: S.vTabs.historial }
	];

	return (
		<>
		<Modal className="waModulo hcVentana" show onHide={borrando ? undefined : onClose} size="xl" centered scrollable fullscreen="sm-down">
			<Modal.Header closeButton={!borrando}>
				<Modal.Title style={{ fontSize: movil ? 15 : 18 }}>
					<div className="hcTituloVentana">
						<span>{f ? nombre : S.cargando}</span>
						{f ? <small>{f.hora}</small> : null}
					</div>
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 15, paddingLeft: movil ? 14 : 22, paddingRight: movil ? 14 : 22 }}>
				{error ? <div className="alert alert-danger">{error}</div> : !f ? <div className="loader" /> : <>
					<div className="hcAcciones" style={{ marginBottom: 14 }}>
						{permisos.editar ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => onEditar(f.id)}>{S.editar}</button> : null}
						{permisos.matricula ? <Link className="btn btn-outline-secondary btn-sm" to={"/enrollmentUpdates?horario=" + f.id}>{S.actualizaciones}</Link> : null}
						{puedeLista ? <Link className="btn btn-primary btn-sm" to={"/attendances?horario=" + f.id}>{S.pasarLista}</Link> : null}
						{hayMenu ? (
							<Dropdown align="end">
								<Dropdown.Toggle as="button" type="button" className="hcMenuBoton" aria-label="Más">
									<FontAwesomeIcon icon={solid("ellipsis")} />
								</Dropdown.Toggle>
								<Dropdown.Menu className="hcMenu" popperConfig={{ strategy: "fixed" }}>
									{permisos.matricula ? <Dropdown.Item as="button" onClick={() => onActualizarMatricula(f)}>{S.actualizarMatricula}</Dropdown.Item> : null}
									{puedeBorrar ? <Dropdown.Item as="button" className="peligro" onClick={() => { setErrorBorrar(null); setConfirmando(true); }}>{S.borrar}</Dropdown.Item> : null}
								</Dropdown.Menu>
							</Dropdown>
						) : null}
					</div>

					{confirmando ? (
						<div className="hcAviso">
							<div style={{ fontWeight: 700, marginBottom: 6 }}>{S.confirmarBorrar(nombre)}</div>
							{errorBorrar ? <div style={{ marginBottom: 6 }}>{errorBorrar}</div> : null}
							<div className="hcAcciones">
								<button type="button" className="btn btn-outline-secondary btn-sm" disabled={borrando} onClick={() => setConfirmando(false)}>{S.cancelar}</button>
								<button type="button" className="btn btn-primary btn-sm" disabled={borrando} onClick={borrar}>{S.borrar}</button>
							</div>
						</div>
					) : null}

					{nombreDistinto ? (
						<div className="hcAviso hcDistinto">
							<div className="hcDistintoTitulo">{S.ch.distinto}</div>
							<div className="hcVersiones">
								<span>{S.ch.nombreDice} <b>{f.horaDelNombre || f.nombre}</b></span>
								<span>{S.ch.clasesEstan} <b>{f.hora}</b></span>
							</div>
							{rehaciendo ? (<>
								<div className="hcVersiones"><span>{S.ch.nombreQueda} <b>{f.nombreDesdeSesiones}</b></span></div>
								{errorNombre ? <div>{errorNombre}</div> : null}
								<div className="hcAcciones">
									<button type="button" className="btn btn-outline-secondary btn-sm" disabled={guardandoNombre} onClick={() => setRehaciendo(false)}>{S.cancelar}</button>
									<button type="button" className="btn btn-primary btn-sm" disabled={guardandoNombre} onClick={usarClases}>{S.ch.confirmar}</button>
								</div>
							</>) : (
								<div className="hcAcciones">
									{f.sesionesDelNombre && f.sesionesDelNombre.length > 0 ? <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setCambiandoHora(true)}>{S.ch.usarNombre}</button> : null}
									<button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => { setErrorNombre(null); setRehaciendo(true); }}>{S.ch.usarClases}</button>
								</div>
							)}
						</div>
					) : null}

					<div className="hcDatos">
						<Dato t={S.datos.estado}><ChipEstado estado={f.estado} /></Dato>
						<Dato t={S.datos.aula}>{f.aula || S.sinAula}{f.edificio ? <span className="hcSub">{f.edificio}</span> : null}</Dato>
						<Dato t={S.datos.maestra}>{f.maestra ? f.maestra : S.sinMaestra}</Dato>
						<Dato t={S.datos.fechas}>{fechasTexto(f.inicio, f.fin)}</Dato>
						<Dato t={S.datos.alumnas}>{f.cupo ? `${f.alumnas} de ${f.cupo}` : f.alumnas}</Dato>
						<Dato t={S.datos.periodo}>{f.periodo || "-"}</Dato>
						{mensualidad ? <Dato t={S.datos.mensualidad}>{mensualidad}</Dato> : null}
						<Dato t={S.datos.codigo}>{f.code}</Dato>
					</div>

					<div className="hcHistoria">
						{f.creadoEl || f.creadoPor ? <span>{S.historia.creado} <b>{fechaDMY(f.creadoEl)}</b>{f.creadoPor ? ` ${S.historia.por} ${f.creadoPor}` : ""}</span> : null}
						{f.ultimaLista ? <span>{S.historia.ultimaLista} <b>{fechaDMY(f.ultimaLista)}</b></span> : null}
						{f.asistencia != null ? <span>{S.historia.asistencia} <b>{f.asistencia} %</b></span> : null}
						{f.kitsPendientes > 0 ? <span className="hcOroTexto">{S.historia.kits(f.kitsPendientes)}</span> : null}
					</div>

					<div className="hcPestanas">
						<ul className="nav nav-pills waTabs" style={{ gap: 4, flexWrap: "wrap" }}>
							{tabs.map((t) => (
								<li className="nav-item" key={t.key}>
									<button type="button" className={"nav-link btn btn-sm " + (tab === t.key ? "active" : "")} onClick={() => setTab(t.key)}>
										{t.label}{t.cuenta ? <span className="waTabCuenta">{t.cuenta}</span> : null}
									</button>
								</li>
							))}
						</ul>
					</div>

					<div className="hcCuerpo">
						{tab === "alumnas" ? (
							!alumnas ? <div className="loader" /> : alumnas.length < 1 ? <div className="hcSub">{S.alumna.sinAlumnas}</div> : (
								<div className="hcScroll"><table className="table table-sm hcTabla">
									<thead><tr><th>{S.alumna.nombre}</th><th>{S.alumna.estado}</th><th className="hcNum">{S.alumna.saldo}</th></tr></thead>
									<tbody>
										{alumnas.map((a: any) => {
											const vencido = Number(a.generalBalance?.expiredMonthlyBalance?.value) > 0;
											return (
												<tr key={a.enrollmentId || a.id}>
													<td>{nombreDeAlumno(a.firstName, a.lastName)}</td>
													<td><span className={"hcChip" + (Number(a.enrollmentStatusId) === 1 ? " acento" : "")}>{a.enrollmentStatus}</span></td>
													<td className={"hcNum" + (vencido ? " hcOroTexto" : "")}>{a.generalBalance?.monthlyBalance ? dinero(a.generalBalance.currency || sym, a.generalBalance.monthlyBalance.value) : ""}</td>
												</tr>
											);
										})}
									</tbody>
								</table></div>
							)
						) : null}

						{tab === "sesiones" ? (<>
							{!sesiones ? <div className="loader" /> : sesiones.length < 1 ? <div className="hcSub">{S.sesiones.sin}</div> : (
								<div className="hcScroll" style={{ maxHeight: 280, overflowY: "auto" }}><table className="table table-sm hcTabla">
									<thead><tr><th>{S.sesiones.clase}</th><th>{S.sesiones.fecha}</th><th>{S.sesiones.hora}</th><th>{S.sesiones.lista}</th></tr></thead>
									<tbody>
										{sesiones.map((s: any) => (
											<tr key={s.sessionId}>
												<td>{s.classNumber}</td>
												<td>{fechaDMY(s.sessionOn)}</td>
												<td>{s.sessionHour}</td>
												<td>{s.hasAttendances ? `${s.presentStudents} de ${s.presentStudents + s.absentStudents}` : ""}</td>
											</tr>
										))}
									</tbody>
								</table></div>
							)}
							<AsuetosHorario campusId={campusId} scheduleId={id} aulas={aulas} puedeEscribir={permisos.editar} />
						</>) : null}

						{tab === "precios" ? (
							preciosFilas.length < 1 ? <div className="hcSub">{S.precios.sin}</div> : (
								<div className="hcPrecios">
									{preciosFilas.map(([k, v]) => <Dato key={k} t={ETIQUETAS_PRECIO[k] || titulo(k)}>{valorPrecio(k, v)}</Dato>)}
								</div>
							)
						) : null}

						{tab === "historial" ? <HistorialHorario campusId={campusId} scheduleId={id} puedeEscribir={permisos.editar} /> : null}
					</div>
				</>}
			</Modal.Body>
		</Modal>
		{cambiandoHora && f && f.sesionesDelNombre ? (
			<CambiarHora
				campusId={campusId} horarioId={f.id} titulo={nombre} sesionesIniciales={f.sesionesDelNombre} editable={false}
				motivoAuto="Usar la hora del nombre del horario"
				onClose={() => setCambiandoHora(false)} onHecho={() => { setCambiandoHora(false); recargarFicha(); }}
			/>
		) : null}
		</>
	);
};

export default VentanaHorario;
